import apiClient, { API_BASE_URL } from './apiClient';
import { MediaUploadResponse, MediaListResponse } from '../types/media.types';
import { logger } from '../utils/logger';

class MediaService {
  private mediaCache = new Map<string, any>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutos
  /**
   * Upload de áudio
   */
  async uploadAudio(conversationId: number, sessionId: number, audioFile: File): Promise<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await apiClient.post(
      `/media/conversations/${conversationId}/sessions/${sessionId}/audio`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  }

  /**
   * Upload de imagem
   */
  async uploadImage(conversationId: number, sessionId: number, imageFile: File): Promise<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await apiClient.post(
      `/media/conversations/${conversationId}/sessions/${sessionId}/image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  }

  /**
   * Obter mídia de uma conversa (com cache)
   */
  async getConversationMedia(conversationId: number, forceRefresh: boolean = false): Promise<MediaListResponse> {
    const cacheKey = `conversation-${conversationId}`;
    const cached = this.mediaCache.get(cacheKey);
    
    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      logger.debug(`📦 Retornando mídia do cache para conversa ${conversationId}`);
      return cached.data;
    }

    const response = await apiClient.get(`/media/conversations/${conversationId}/media`);
    
    // Armazenar no cache
    this.mediaCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });

    return response.data;
  }

  /**
   * Obter URL absoluta do arquivo de mídia (rota pública, sem auth).
   * Usa a mesma base da API para que img/audio carreguem corretamente em qualquer origem.
   */
  getMediaUrl(mediaId: number, fileName?: string): string {
    const base = API_BASE_URL.replace(/\/$/, '');
    if (fileName && fileName.trim()) {
      const normalizedFileName = encodeURIComponent(fileName.trim());
      return `${base}/media/${mediaId}?filename=${normalizedFileName}`;
    }
    return `${base}/media/${mediaId}`;
  }

  /**
   * Remover mídia
   */
  async deleteMedia(mediaId: number): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/media/${mediaId}`);
    
    // Limpar cache relacionado
    this.clearCache();
    
    return response.data;
  }

  /**
   * Limpar cache de mídia
   */
  clearCache(): void {
    this.mediaCache.clear();
    logger.debug('🧹 Cache de mídia limpo');
  }

  /**
   * Invalidar cache de uma conversa específica
   */
  invalidateConversationCache(conversationId: number): void {
    const cacheKey = `conversation-${conversationId}`;
    this.mediaCache.delete(cacheKey);
    logger.debug(`🧹 Cache invalidado para conversa ${conversationId}`);
  }

  /**
   * Obter estatísticas do cache
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.mediaCache.size,
      keys: Array.from(this.mediaCache.keys())
    };
  }
}

export const mediaService = new MediaService();
